/**
 * useAgentChat - Primary chat hook with AI SDK 6 integration
 *
 * This hook provides the main chat interface, using AI SDK 6's useChat
 * with custom transports for our backend format.
 *
 * Architecture:
 * - Uses LegacyBackendTransport for standard JSON streaming
 * - Uses MarkdownStreamTransport for Query Analysis mode
 * - Dispatches to Redux for component consumption
 * - V1 fallback for proceed endpoint (isResponseToPrompt)
 */

import { useChat, type UIMessage } from "@ai-sdk/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  ChatResponse,
  LoadingState,
  MessageAttachment,
  ChatMessage,
} from "../types";
import { useChatContext } from "../ChatProvider";
import {
  setAbortController,
  clearActions,
  addAction,
  setLoadingState,
  addMessage,
  setError,
  updateContext,
  setTodos,
  clearTodos,
} from "../store/chatSlice";
import { processAgentStream } from "../utils/streamProcessor";
import {
  AccumulatedStreamData,
  createEmptyAccumulatedData,
  LegacyBackendTransport,
  MarkdownStreamTransport,
  TransportDataCallback,
} from "./transports";
import { ChatRequestOptions } from "ai";
import { PromptInputMessage } from "@altimateai/lego";

// Re-export types for consumers
export type { UIMessage, AccumulatedStreamData };

// ==========================================================================
// HOOK RETURN TYPE
// ==========================================================================

/**
 * Return type for useAgentChat hook
 */
export interface UseAgentChatReturn {
  sendMessage: (params: {
    message: string;
    isResponseToPrompt?: boolean;
    context?: Record<string, unknown>;
    dontAddUserMessage?: boolean;
    files?: PromptInputMessage["files"][number][];
  }) => Promise<ChatResponse>;
  updateContextAndSendMessage: (
    message: string,
    context: Record<string, unknown>,
  ) => Promise<void>;
  regenerate: (
    params?: {
      messageId?: string;
    } & ChatRequestOptions,
  ) => Promise<void>;
  loadV1Session: (v1Messages: ChatMessage[]) => void;
  getV1Messages: () => ChatMessage[];
  messages: UIMessage[];
  status: string;
  accumulatedDataRef: React.MutableRefObject<AccumulatedStreamData>;
}

// ==========================================================================
// V1 COMPATIBILITY UTILITIES
// Convert between AI SDK UIMessage format and V1 ChatMessage/ChatResponse
// ==========================================================================

/**
 * Extract text content from UIMessage parts array
 */
const extractTextFromParts = (parts: UIMessage["parts"]): string => {
  return (
    parts
      ?.filter((p): p is { type: "text"; text: string } => p.type === "text")
      .map((p) => p.text)
      .join("") || ""
  );
};

/**
 * Convert AccumulatedStreamData to V1 ChatResponse.
 */
const toV1ChatResponse = (accData: AccumulatedStreamData): ChatResponse => ({
  content: accData.content,
  role: "assistant",
  timestamp: Date.now(),
  heading: accData.heading || "",
  actions: accData.actions,
  artifacts: accData.artifacts,
  todos: accData.todos.length > 0 ? accData.todos : undefined,
  interaction: accData.interaction,
});

/**
 * Convert V1 ChatMessage to V2 UIMessage format.
 */
const fromV1ChatMessage = (message: ChatMessage): UIMessage => ({
  id: message.id,
  role: message.role,
  parts: [{ type: "text" as const, text: message.content }],
});

/**
 * Convert array of V1 ChatMessages to V2 UIMessages.
 */
const fromV1ChatMessages = (messages: ChatMessage[]): UIMessage[] => {
  return messages.map(fromV1ChatMessage);
};

/**
 * Extract AccumulatedStreamData from a V1 ChatMessage.
 */
const extractAccumulatedDataFromV1 = (
  message: ChatMessage,
): AccumulatedStreamData => ({
  content: message.content,
  actions: message.actions || [],
  artifacts: message.artifacts || [],
  citations: message.citations || [],
  todos: message.todos || [],
  interaction: message.interaction,
  heading: message.heading || "",
  confidence_str: message.confidence_str,
});

// ==========================================================================
// MAIN HOOK
// ==========================================================================

export const useAgentChat = (): UseAgentChatReturn => {
  const {
    dispatch,
    currentSessionId,
    sessions,
    initialValidation,
    urls,
    requestParams,
    isMarkdownResponse,
  } = useChatContext();

  // ==========================================================================
  // ACCUMULATED DATA MANAGEMENT
  // ==========================================================================

  // Accumulated data from the current/last stream
  const accumulatedDataRef = useRef<AccumulatedStreamData>(
    createEmptyAccumulatedData(),
  );

  // Pending request resolution - used to await stream completion
  const pendingRequestRef = useRef<{
    resolve: (_response: ChatResponse) => void;
    reject: (_error: Error) => void;
  } | null>(null);

  // Track dispatched actions/todos/content to avoid duplicates during progressive updates
  const dispatchedActionsCountRef = useRef<number>(0);
  const lastDispatchedContentLengthRef = useRef<number>(0);
  // Stable message ID for the current streaming message (upsert pattern)
  const streamingMessageIdRef = useRef<string | null>(null);

  // Request ID tracking to prevent race conditions after cancel
  // When a request is cancelled and a new one starts, old callbacks might still arrive
  // We use this to detect and ignore stale callbacks
  const currentRequestIdRef = useRef<number>(0);
  const callbackRequestIdRef = useRef<number>(0);

  // Refs to avoid stale closures in the transport callback
  const dispatchRef = useRef(dispatch);
  const currentSessionIdRef = useRef(currentSessionId);

  // Keep refs in sync with current values
  useEffect(() => {
    dispatchRef.current = dispatch;
    currentSessionIdRef.current = currentSessionId;
  }, [dispatch, currentSessionId]);

  // Stable callback for transport to call directly (avoids global Map issues)
  const handleTransportData = useCallback<TransportDataCallback>(
    (data: Partial<AccumulatedStreamData>) => {
      // Guard against stale callbacks from cancelled requests
      // When a request is cancelled and a new one starts immediately, old callbacks might still arrive
      // We detect this by comparing the callback's request ID with the current active request
      if (callbackRequestIdRef.current !== currentRequestIdRef.current) {
        // This callback is from an old cancelled request, ignore it
        return;
      }

      // Merge new data into accumulated data
      const current = accumulatedDataRef.current;
      accumulatedDataRef.current = {
        content: data.content ?? current.content,
        actions: data.actions ?? current.actions,
        artifacts: data.artifacts ?? current.artifacts,
        citations: data.citations ?? current.citations,
        todos: data.todos ?? current.todos,
        interaction: data.interaction ?? current.interaction,
        heading: data.heading ?? current.heading,
        confidence_str: data.confidence_str ?? current.confidence_str,
      };

      // Progressive streaming dispatch
      const accData = accumulatedDataRef.current;
      const currentDispatch = dispatchRef.current;
      const sessionId = currentSessionIdRef.current;

      // Dispatch new actions that haven't been dispatched yet
      if (accData.actions.length > dispatchedActionsCountRef.current) {
        const newActions = accData.actions.slice(
          dispatchedActionsCountRef.current,
        );
        newActions.forEach((action) => {
          currentDispatch(addAction(action));
        });
        dispatchedActionsCountRef.current = accData.actions.length;
      }

      // Dispatch new todos that haven't been dispatched yet
      currentDispatch(setTodos(accData.todos));

      // Progressive content streaming
      if (
        accData.content.length > lastDispatchedContentLengthRef.current &&
        sessionId &&
        streamingMessageIdRef.current
      ) {
        const contentLenDiff =
          accData.content.length - lastDispatchedContentLengthRef.current;
        // Throttle updates: only dispatch if content grew by at least 50 chars
        if (contentLenDiff >= 50 || accData.content.length < 50) {
          currentDispatch(
            addMessage({
              sessionId,
              id: streamingMessageIdRef.current,
              content: accData.content,
              role: "assistant",
              timestamp: Date.now(),
              heading: accData.heading || "",
              actions: accData.actions || [],
              artifacts: accData.artifacts || [],
              citations: accData.citations || [],
              todos: accData.todos || [],
              interaction: accData.interaction,
              confidence_str: accData.confidence_str,
            }),
          );
          lastDispatchedContentLengthRef.current = accData.content.length;
        }
      }
    },
    [],
  ); // Empty deps - uses refs for all changing values

  // ==========================================================================
  // TRANSPORT SETUP
  // ==========================================================================

  const currentSession = sessions[currentSessionId || ""];
  const currentContext = currentSession?.context || {};
  const apiUrl = `${urls?.origin || ""}${urls?.askPath || "/agent/ask"}`;

  const headersRecord = useMemo((): Record<string, string> | undefined => {
    if (!requestParams?.headers) return undefined;
    if (requestParams.headers instanceof Headers) {
      return Object.fromEntries(requestParams.headers.entries());
    }
    if (Array.isArray(requestParams.headers)) {
      return Object.fromEntries(requestParams.headers);
    }
    return requestParams.headers as Record<string, string>;
  }, [requestParams?.headers]);

  // Fresh session data pattern - store in ref so transport can access current values
  // Updated synchronously during render (before any effects) to ensure getBody always has latest data
  const sessionDataRef = useRef({
    context: currentContext,
    session_id: currentSessionId,
    model_type: currentSession?.selectedModel,
    mode: "ask",
    datamates: currentSession?.datamates,
  });

  // Update ref synchronously during render - this ensures getBody() always reads the latest data
  // even if called immediately after a re-render (before effects run)
  sessionDataRef.current = {
    context: currentSession?.context || {},
    session_id: currentSessionId,
    model_type: currentSession?.selectedModel,
    mode: "ask",
    datamates: currentSession?.datamates,
  };

  const getBody = useCallback((): Record<string, unknown> => {
    const body = { ...sessionDataRef.current };
    // Filter out null/undefined model_type to maintain backwards compatibility
    if (body.model_type === null || body.model_type === undefined) {
      delete body.model_type;
    }
    return body;
  }, []);

  const transport = useMemo(() => {
    return isMarkdownResponse
      ? new MarkdownStreamTransport({
          api: apiUrl,
          headers: headersRecord,
          getBody,
          onData: handleTransportData,
        })
      : new LegacyBackendTransport({
          api: apiUrl,
          headers: headersRecord,
          getBody,
          onData: handleTransportData,
        });
  }, [apiUrl, isMarkdownResponse, headersRecord, getBody, handleTransportData]);

  // ==========================================================================
  // AI SDK useChat INTEGRATION
  // ==========================================================================

  const {
    messages,
    setMessages,
    sendMessage: sdkSendMessage,
    status,
    stop: sdkStop,
    regenerate: sdkRegenerate,
  } = useChat({
    messages: fromV1ChatMessages(currentSession?.messages || []),
    id: currentSessionId || undefined,
    transport,
    onFinish: () => {
      // Resolve pending request with V1-compatible ChatResponse
      if (pendingRequestRef.current) {
        const response = toV1ChatResponse(accumulatedDataRef.current);
        pendingRequestRef.current.resolve(response);
        pendingRequestRef.current = null;
      }
    },
    onError: (err) => {
      // Don't reject for AbortError - the error will propagate through sdkSendMessage throw
      // This prevents race conditions where a newer request has started and
      // overwritten pendingRequestRef before this (old) request's onError is called
      const isAbortError = err instanceof Error && err.name === "AbortError";
      if (isAbortError) {
        return;
      }
      if (pendingRequestRef.current) {
        pendingRequestRef.current.reject(
          err instanceof Error ? err : new Error(String(err)),
        );
        pendingRequestRef.current = null;
      }
    },
  });

  // Track LATEST messages for synchronous access (fixes stale closure issue)
  const latestMessagesRef = useRef<UIMessage[]>([]);

  useEffect(() => {
    latestMessagesRef.current = [...messages];
  }, [messages]);

  // ==========================================================================
  // HELPER FUNCTIONS
  // ==========================================================================

  const createAttachmentsFromContext = (
    files: PromptInputMessage["files"][number][],
  ): MessageAttachment[] => {
    const attachmentsData: MessageAttachment[] = [];

    if (files.length > 0) {
      files.forEach(
        (file: PromptInputMessage["files"][number], index: number) => {
          attachmentsData.push({
            id: `temp-${Date.now()}-${index}`,
            attachment_name: file.filename || "unnamed",
            file_type: file.mediaType || null,
            s3_url: null,
            extracted_content: null,
            order: index,
            attachment_type: "file",
            attachment_reference_id: null,
            file_size: file.size || null,
            created_on: new Date().toISOString(),
          });
        },
      );
    }

    return attachmentsData;
  };

  // ==========================================================================
  // V1 COMPATIBILITY API
  // ==========================================================================

  /**
   * Load an existing V1 session.
   */
  const loadV1Session = useCallback(
    (v1Messages: ChatMessage[]) => {
      const uiMessages = fromV1ChatMessages(v1Messages);
      setMessages(uiMessages);

      const lastAssistant = [...v1Messages]
        .reverse()
        .find((m) => m.role === "assistant");
      if (lastAssistant) {
        accumulatedDataRef.current =
          extractAccumulatedDataFromV1(lastAssistant);
      } else {
        accumulatedDataRef.current = createEmptyAccumulatedData();
      }
    },
    [setMessages],
  );

  /**
   * Get all messages in V1 ChatMessage format.
   */
  const getV1Messages = useCallback((): ChatMessage[] => {
    return messages.map((msg, index) => {
      const isLastAssistant =
        msg.role === "assistant" && index === messages.length - 1;
      const accData = isLastAssistant
        ? accumulatedDataRef.current
        : createEmptyAccumulatedData();

      return {
        id: msg.id,
        content: accData.content || extractTextFromParts(msg.parts),
        role: msg.role as "user" | "assistant",
        timestamp: Date.now(),
        heading: accData.heading || "",
        actions: accData.actions,
        artifacts: accData.artifacts,
        citations: accData.citations.length > 0 ? accData.citations : undefined,
        todos: accData.todos.length > 0 ? accData.todos : undefined,
        interaction: accData.interaction,
        confidence_str: accData.confidence_str,
      };
    });
  }, [messages]);

  // ==========================================================================
  // MAIN SEND MESSAGE FUNCTION
  // ==========================================================================

  const updateContextAndSendMessage = async (
    message: string,
    context: Record<string, unknown>,
  ) => {
    dispatch(
      updateContext({
        sessionId: currentSessionId!,
        context,
      }),
    );
    await sendMessage({
      message,
      context,
    });
  };

  const sendMessage = async ({
    message,
    isResponseToPrompt = false,
    context: newContext,
    dontAddUserMessage = false,
    files = [],
  }: {
    message: string;
    isResponseToPrompt?: boolean;
    context?: Record<string, unknown>;
    dontAddUserMessage?: boolean;
    files?: PromptInputMessage["files"][number][];
  }): Promise<ChatResponse> => {
    if (!currentSessionId) {
      throw new Error("No active session");
    }

    const context = {
      ...(sessions[currentSessionId]?.context || {}),
      ...newContext,
    };

    // Add user message with attachments
    if (!dontAddUserMessage) {
      const attachments = createAttachmentsFromContext(files);
      dispatch(
        addMessage({
          sessionId: currentSessionId,
          id: Date.now().toString(),
          content: message,
          role: "user",
          timestamp: Date.now(),
          heading: "",
          attachments: attachments.length > 0 ? attachments : undefined,
        }),
      );
    }

    // Create new AbortController and clear previous actions and todos
    const abortController = new AbortController();

    // Connect our abort controller to the AI SDK's stop function
    // When CancelGenerationButton aborts our controller, this will also stop the SDK request
    const handleAbort = () => {
      sdkStop();
    };
    abortController.signal.addEventListener("abort", handleAbort);

    dispatch(
      setLoadingState({
        state: LoadingState.LOADING,
        sessionId: currentSessionId,
      }),
    );
    dispatch(setAbortController(abortController));
    dispatch(clearActions());
    dispatch(clearTodos());

    // Reset progressive dispatch counters for new message
    dispatchedActionsCountRef.current = 0;
    lastDispatchedContentLengthRef.current = 0;
    streamingMessageIdRef.current = (Date.now() + 1).toString();

    // Increment request ID to invalidate any pending callbacks from previous requests
    // This must happen BEFORE resetting accumulated data so stale callbacks are ignored
    currentRequestIdRef.current++;
    callbackRequestIdRef.current = currentRequestIdRef.current;

    // Reset accumulated data for new request
    accumulatedDataRef.current = createEmptyAccumulatedData();

    let hasError = false;
    try {
      await initialValidation?.({ context });

      // PRIMARY PATH: AI SDK 6 (for normal requests)
      if (!isResponseToPrompt) {
        // Create a promise that resolves when onFinish is called
        const responsePromise = new Promise<ChatResponse>((resolve, reject) => {
          pendingRequestRef.current = { resolve, reject };
        });

        // Send via AI SDK
        if (files.length) {
          await sdkSendMessage({
            text: message,
            files,
          });
        } else {
          await sdkSendMessage({
            role: "user",
            parts: [{ type: "text", text: message }],
          });
        }

        // Wait for stream completion
        const v2Response = await responsePromise;

        // Dispatch final message to Redux
        const accData = accumulatedDataRef.current;
        const finalId =
          streamingMessageIdRef.current || (Date.now() + 1).toString();
        dispatch(
          addMessage({
            sessionId: currentSessionId,
            id: finalId,
            content: v2Response.content,
            role: "assistant",
            timestamp: Date.now(),
            interaction: v2Response.interaction,
            heading: v2Response.heading,
            actions: v2Response.actions,
            artifacts: v2Response.artifacts,
            citations: accData?.citations,
            todos: v2Response.todos,
            confidence_str: accData?.confidence_str,
          }),
        );
        streamingMessageIdRef.current = null;

        return v2Response;
      }

      // ==========================================================================
      // FALLBACK: V1 for proceed endpoint (isResponseToPrompt)
      // ==========================================================================

      const data = {
        values: message,
        session_id: currentSessionId,
      };

      const url = urls?.origin || "https://api.getaltimate.com";
      const response = await fetch(
        `${url}${urls?.proceedPath || "/agent/proceed"}`,
        {
          ...requestParams,
          method: "POST",
          headers: {
            ...requestParams?.headers,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
          signal: abortController.signal,
        },
      );

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response stream available");
      }

      const id = (Date.now() + 1).toString();
      let isAlreadyStreamed = false;

      const streamResult = await processAgentStream(reader, {
        isMarkdownResponse,
        callbacks: {
          onAction: (action) => {
            dispatch(addAction(action));
          },
          onCitation: () => {
            // Citations handled in onFinalResponse
          },
          onTodos: (todos) => {
            dispatch(setTodos(todos));
          },
          onMarkdownChunk: (content: string) => {
            isAlreadyStreamed = true;
            dispatch(
              addMessage({
                sessionId: currentSessionId,
                id,
                content,
                role: "assistant",
                timestamp: Date.now(),
                heading: "Markdown Response",
                actions: [],
                artifacts: [],
                citations: undefined,
                todos: undefined,
              }),
            );
          },
          onError: (error) => {
            console.error("Stream processing error:", error);
          },
          onComplete: () => {
            // Stream processing complete
          },
        },
      });

      const finalResponse: ChatResponse = {
        content: streamResult.content,
        role: "assistant",
        timestamp: Date.now(),
        actions: streamResult.actions,
        artifacts: streamResult.artifacts || [],
        heading:
          streamResult.heading ||
          (isMarkdownResponse ? "Markdown Response" : ""),
        interaction: streamResult.interaction,
        todos: streamResult.todos,
      };

      if (!isAlreadyStreamed) {
        const finalId = (Date.now() + 1).toString();
        dispatch(
          addMessage({
            sessionId: currentSessionId,
            id: finalId,
            content: finalResponse.content,
            role: "assistant",
            timestamp: Date.now(),
            interaction: finalResponse.interaction,
            heading: finalResponse.heading,
            actions: finalResponse.actions,
            artifacts: finalResponse.artifacts,
            citations: streamResult.citations,
            todos: finalResponse.todos,
            confidence_str: streamResult.confidence_str,
          }),
        );
      }

      return finalResponse;
    } catch (error) {
      const isAbortError =
        error instanceof Error && error.name === "AbortError";
      hasError = true;

      if (!isAbortError) {
        // Handle non-abort errors gracefully - show error in UI via setError
        // RefetchSession component will display the error message
        const errorMessage =
          error instanceof Error ? error.message : "Failed to send message";
        dispatch(setError(errorMessage));
        // Don't rethrow - error is handled and shown in UI
        // Return undefined to signal no response (but not an unhandled error)
        return undefined as unknown as ChatResponse;
      }
      throw error;
    } finally {
      // Clean up abort event listener
      abortController.signal.removeEventListener("abort", handleAbort);

      if (!abortController.signal.aborted) {
        dispatch(setAbortController(null));
        dispatch(
          setLoadingState({
            state: hasError ? LoadingState.ERROR : LoadingState.LOADED,
            sessionId: currentSessionId,
          }),
        );
      }

      // Clear uploaded_files from context after request completes (success, error, or cancel)
      // This must happen AFTER the request so the transport doesn't read the cleared value
      if (!dontAddUserMessage) {
        dispatch(
          updateContext({
            sessionId: currentSessionId,
            context: { ...context, uploaded_files: [] },
          }),
        );
      }
    }
  };

  return {
    sendMessage,
    updateContextAndSendMessage,
    regenerate: sdkRegenerate,
    // V1 compatibility
    loadV1Session,
    getV1Messages,
    // AI SDK state (for advanced use cases)
    messages,
    status,
    accumulatedDataRef,
  };
};
